export const MODEL_TYPES = {
    LLM: "llm",
    MODERATION: "moderation",
    RERANK: "rerank",
    SPEECH_TO_TEXT: "speech2text",
    TEXT_EMBEDDING: "text-embedding",
    TEXT_TO_IMAGE: "text-to-image",
    IMAGE_TO_IMAGE: "image-to-image",
    TEXT_TO_VIDEO: "text-to-video",
    IMAGE_TO_VIDEO: "image-to-video",
    REFERENCE_TO_VIDEO: "reference-to-video",
    VIDEO_TO_VIDEO: "video-to-video",
    TTS: "tts",
} as const;

export type ModelType = (typeof MODEL_TYPES)[keyof typeof MODEL_TYPES];

export const MODEL_TYPE_DESCRIPTIONS: Record<
    ModelType,
    { name: string; nameEn: string; description: string }
> = {
    [MODEL_TYPES.LLM]: {
        name: "大语言模型",
        nameEn: "LLM",
        description: "文本生成、对话、推理等",
    },
    [MODEL_TYPES.MODERATION]: {
        name: "内容审核",
        nameEn: "MODERATION",
        description: "文本内容安全检测",
    },
    [MODEL_TYPES.RERANK]: {
        name: "重排序",
        nameEn: "RERANK",
        description: "文档相关性重排序",
    },
    [MODEL_TYPES.SPEECH_TO_TEXT]: {
        name: "语音识别",
        nameEn: "SPEECH_TO_TEXT",
        description: "语音转文本 (STT)",
    },
    [MODEL_TYPES.TEXT_EMBEDDING]: {
        name: "文本嵌入",
        nameEn: "TEXT_EMBEDDING",
        description: "文本向量化",
    },
    [MODEL_TYPES.TEXT_TO_IMAGE]: {
        name: "图像生成",
        nameEn: "TEXT_TO_IMAGE",
        description: "文本生成图像",
    },
    [MODEL_TYPES.IMAGE_TO_IMAGE]: {
        name: "图生图",
        nameEn: "IMAGE_TO_IMAGE",
        description: "参考图生成图像",
    },
    [MODEL_TYPES.TEXT_TO_VIDEO]: {
        name: "视频生成",
        nameEn: "TEXT_TO_VIDEO",
        description: "文本生成视频",
    },
    [MODEL_TYPES.IMAGE_TO_VIDEO]: {
        name: "图生视频",
        nameEn: "IMAGE_TO_VIDEO",
        description: "参考图生成视频",
    },
    [MODEL_TYPES.REFERENCE_TO_VIDEO]: {
        name: "参考图生视频",
        nameEn: "REFERENCE_TO_VIDEO",
        description: "多张参考图生成视频",
    },
    [MODEL_TYPES.VIDEO_TO_VIDEO]: {
        name: "视频生视频",
        nameEn: "VIDEO_TO_VIDEO",
        description: "参考素材或视频生成视频",
    },
    [MODEL_TYPES.TTS]: {
        name: "语音合成",
        nameEn: "TTS",
        description: "文本转语音 (TTS)",
    },
};

export const MODEL_CATEGORIES = {
    TEXT: "text",
    IMAGE: "image",
    VIDEO: "video",
    AUDIO: "audio",
    EMBEDDING: "embedding",
} as const;

export type ModelCategory = (typeof MODEL_CATEGORIES)[keyof typeof MODEL_CATEGORIES];

export const MODEL_CATEGORY_DESCRIPTIONS: Record<
    ModelCategory,
    { name: string; nameEn: string; description: string }
> = {
    [MODEL_CATEGORIES.TEXT]: {
        name: "文本",
        nameEn: "Text",
        description: "对话、推理、内容生成",
    },
    [MODEL_CATEGORIES.IMAGE]: {
        name: "图像",
        nameEn: "Image",
        description: "图像生成",
    },
    [MODEL_CATEGORIES.VIDEO]: {
        name: "视频",
        nameEn: "Video",
        description: "视频生成",
    },
    [MODEL_CATEGORIES.AUDIO]: {
        name: "音频",
        nameEn: "Audio",
        description: "语音合成、语音识别",
    },
    [MODEL_CATEGORIES.EMBEDDING]: {
        name: "辅助",
        nameEn: "Auxiliary",
        description: "向量化、重排序、内容审核",
    },
};

export const MODEL_TYPE_TO_CATEGORY: Record<ModelType, ModelCategory> = {
    [MODEL_TYPES.LLM]: MODEL_CATEGORIES.TEXT,
    [MODEL_TYPES.TEXT_TO_IMAGE]: MODEL_CATEGORIES.IMAGE,
    [MODEL_TYPES.IMAGE_TO_IMAGE]: MODEL_CATEGORIES.IMAGE,
    [MODEL_TYPES.TEXT_TO_VIDEO]: MODEL_CATEGORIES.VIDEO,
    [MODEL_TYPES.IMAGE_TO_VIDEO]: MODEL_CATEGORIES.VIDEO,
    [MODEL_TYPES.REFERENCE_TO_VIDEO]: MODEL_CATEGORIES.VIDEO,
    [MODEL_TYPES.VIDEO_TO_VIDEO]: MODEL_CATEGORIES.VIDEO,
    [MODEL_TYPES.TTS]: MODEL_CATEGORIES.AUDIO,
    [MODEL_TYPES.SPEECH_TO_TEXT]: MODEL_CATEGORIES.AUDIO,
    [MODEL_TYPES.TEXT_EMBEDDING]: MODEL_CATEGORIES.EMBEDDING,
    [MODEL_TYPES.RERANK]: MODEL_CATEGORIES.EMBEDDING,
    [MODEL_TYPES.MODERATION]: MODEL_CATEGORIES.EMBEDDING,
};

export function deriveCategory(modelType: ModelType): ModelCategory {
    return MODEL_TYPE_TO_CATEGORY[modelType] ?? MODEL_CATEGORIES.TEXT;
}

export function getAllModelTypes(): ModelType[] {
    return Object.values(MODEL_TYPES);
}

export function getModelTypesWithDescriptions(): Array<{
    type: ModelType;
    name: string;
    description: string;
}> {
    return getAllModelTypes().map((type) => ({
        type,
        ...MODEL_TYPE_DESCRIPTIONS[type],
    }));
}
